import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Table,
  Badge,
  Progress,
  Input,
  FormGroup,
  InputGroup,
  InputGroupAddon,
  InputGroupText
} from 'reactstrap';
import classnames from 'classnames';
import { fetchUserPerformance, fetchPeerDistribution } from '../../../redux/QuestionBank/TestProgressAction';
import { fetchSubjects, fetchUnits, fetchTopics } from '../../../redux/QuestionBank/SubjectsUnitsAction';
import { fetchQuestions } from '../../../redux/QuestionBank/QuestionsAction';
import Header from 'components/Headers/Header';
import ActivityStreaksCard from '../../../components/QuestionBank/ActivityStreaksCard';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import AccessRestriction from '../../../components/AccessRestriction/AccessRestriction';
import TrialBanner from '../../../components/Trial/TrialBanner';
import BoardsCountdownBanner from '../../../components/Trial/BoardsCountdownBanner';
import AddonCrossSellBanner from '../../../components/Billing/AddonCrossSellBanner';
import { getDisplayName } from 'util/specialtyDisplayHelper';

const Performance = () => {
  const dispatch = useDispatch();
  const { userPerformance, peerDistribution, loading: testsLoading } = useSelector(state => state.testProgress);
  const { subjects } = useSelector(state => state.questionBankSubjects);
  const { units } = useSelector(state => state.questionBankUnits);
  const { topics } = useSelector(state => state.questionBankTopics);
  const { questions, loading: questionsLoading } = useSelector(state => state.questionBankQuestions);
  const userHandle = useSelector(state => state.loginAuth?.userDetails?.handle);
  const activeSpecialty = useSelector(state => state.specialty.activeSpecialty);

  const loading = testsLoading || questionsLoading;
  const [activeTab, setActiveTab] = useState('overall');
  const [expandedSubjects, setExpandedSubjects] = useState({});
  const [expandedUnits, setExpandedUnits] = useState({});
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [testDateRange, setTestDateRange] = useState({ start: '', end: '' });
  const [activityView, setActivityView] = useState('week'); // 'week' | 'month' | 'year'
  const [periodOffset, setPeriodOffset] = useState(0); // 0 = current, -1 = previous, etc.

  useEffect(() => {
    if (userHandle) {
      dispatch(fetchUserPerformance());
      dispatch(fetchPeerDistribution());
    }
    dispatch(fetchSubjects());
    dispatch(fetchUnits());
    dispatch(fetchTopics());
    dispatch(fetchQuestions());
  }, [dispatch, userHandle, activeSpecialty]);

  const toggleSubjectExpand = (subjectId) => {
    setExpandedSubjects(prev => ({
      ...prev,
      [subjectId]: !prev[subjectId]
    }));
  };

  const toggleUnitExpand = (unitId) => {
    setExpandedUnits(prev => ({
      ...prev,
      [unitId]: !prev[unitId]
    }));
  };

  // Helper function to format ordinal suffix (1st, 2nd, 3rd, 4th, etc.)
  const getOrdinalSuffix = (num) => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return num + 'st';
    if (j === 2 && k !== 12) return num + 'nd';
    if (j === 3 && k !== 13) return num + 'rd';
    return num + 'th';
  };

  // Check if we have cached performance data
  const hasPerformanceData = !!userPerformance;
  const cachedOverall = userPerformance?.overallStats || {};
  const cachedTestSummaries = userPerformance?.testSummaries || [];

  // #11: Build lookup maps for O(1) resolution instead of O(n) per question
  const questionsMap = useMemo(() => {
    const map = {};
    questions?.forEach(q => { map[q.questionId] = q; });
    return map;
  }, [questions]);

  // Helper: compute real percentile from histogram buckets
  const computePercentile = (userPercent, buckets, totalUsers) => {
    if (!buckets || !totalUsers || totalUsers <= 0) return null;
    const userBucket = Math.min(19, Math.floor(userPercent / 5));
    let usersBelow = 0;
    for (let i = 0; i < userBucket; i++) usersBelow += buckets[i] || 0;
    usersBelow += Math.floor((buckets[userBucket] || 0) / 2); // half of same bucket
    return Math.min(99, Math.max(1, Math.round((usersBelow / totalUsers) * 100)));
  };

  // Calculate overall statistics from cached data + real peer distribution
  const overallStats = useMemo(() => {
    const totalAvailableQuestions = questions?.length || 0;

    if (!hasPerformanceData || !cachedOverall.completedTests) {
      return {
        averageScore: 0, totalCorrect: 0, totalIncorrect: 0, totalOmitted: 0,
        usedQuestions: 0, unusedQuestions: totalAvailableQuestions, totalAvailableQuestions,
        testsCreated: cachedOverall.totalTests || 0, testsCompleted: 0,
        suspendedTests: cachedOverall.suspendedTests || 0,
        yourScore: 0, medianScore: 0, avgTimeSpent: 0, othersAvgTimeSpent: 0,
        othersAvgScore: null, peerPercentile: null
      };
    }

    const totalCorrect = cachedOverall.totalCorrect || 0;
    const totalIncorrect = cachedOverall.totalIncorrect || 0;
    const totalOmitted = cachedOverall.totalOmitted || 0;
    const testsCompleted = cachedOverall.completedTests || 0;
    const usedQuestions = Math.min(cachedOverall.uniqueQuestionsUsed || 0, totalAvailableQuestions);
    const unusedQuestions = Math.max(0, totalAvailableQuestions - usedQuestions);

    const avgTimeSpent = cachedOverall.totalTimeTrackedQuestions > 0
      ? Math.round(cachedOverall.totalTimeSpent / cachedOverall.totalTimeTrackedQuestions) : 0;

    // Calculate others' average time from question analytics
    let othersTimeSum = 0;
    let othersTimeCount = 0;
    questions?.forEach(q => {
      if (q.averageTimeSpent && q.averageTimeSpent > 0) {
        othersTimeSum += q.averageTimeSpent;
        othersTimeCount++;
      }
    });
    const othersAvgTimeSpent = othersTimeCount > 0 ? Math.round(othersTimeSum / othersTimeCount) : 0;

    // Calculate median score from test summaries
    const sortedScores = cachedTestSummaries.map(t => t.score || 0).sort((a, b) => a - b);
    const midIndex = Math.floor(sortedScores.length / 2);
    const medianScore = sortedScores.length > 0
      ? (sortedScores.length % 2 === 0
        ? Math.round((sortedScores[midIndex - 1] + sortedScores[midIndex]) / 2)
        : sortedScores[midIndex])
      : 0;

    // User's overall correct rate
    const totalAnswered = totalCorrect + totalIncorrect + totalOmitted;
    const userCorrectPercent = totalAnswered > 0 ? Math.round((totalCorrect / totalAnswered) * 100) : 0;

    // Peers' average and percentile — prefer real distribution data
    let othersAvgScore = null;
    let peerPercentile = null;

    if (peerDistribution?.available && peerDistribution.totalUsers > 0) {
      // Real peers-only average: subtract current user's own data
      const userOwnCorrect = totalCorrect;
      const userOwnAnswered = totalCorrect + totalIncorrect;
      const peersCorrect = (peerDistribution.overallTotalCorrect || 0) - userOwnCorrect;
      const peersAnswered = (peerDistribution.overallTotalAnswered || 0) - userOwnAnswered;
      othersAvgScore = peersAnswered > 0 ? Math.round((peersCorrect / peersAnswered) * 100) : null;

      // Real percentile from histogram
      peerPercentile = computePercentile(userCorrectPercent, peerDistribution.overallBuckets, peerDistribution.totalUsers);
    } else {
      // Fallback: old heuristic from question analytics
      let totalTimesCorrect = 0;
      let totalTimesAnswered = 0;
      questions?.forEach(q => {
        totalTimesCorrect += q.timesCorrect || 0;
        totalTimesAnswered += q.timesAnswered || 0;
      });
      othersAvgScore = totalTimesAnswered > 0 ? Math.round((totalTimesCorrect / totalTimesAnswered) * 100) : null;

      if (othersAvgScore !== null) {
        const difference = userCorrectPercent - othersAvgScore;
        peerPercentile = Math.min(99, Math.max(1, Math.round(50 + difference)));
      }
    }

    return {
      averageScore: userCorrectPercent,
      totalCorrect, totalIncorrect, totalOmitted,
      usedQuestions, unusedQuestions, totalAvailableQuestions,
      testsCreated: cachedOverall.totalTests || 0, testsCompleted,
      suspendedTests: cachedOverall.suspendedTests || 0,
      yourScore: userCorrectPercent, medianScore, avgTimeSpent, othersAvgTimeSpent,
      othersAvgScore, peerPercentile
    };
  }, [hasPerformanceData, cachedOverall, cachedTestSummaries, questions, peerDistribution]);

  // Calculate subject performance from cached data + P-Rank from real distribution or fallback
  const subjectPerformance = useMemo(() => {
    const cachedSubjectPerf = userPerformance?.subjectPerformance || {};
    const peerSubjectDists = peerDistribution?.available ? (peerDistribution.subjectDistributions || {}) : {};

    // Calculate available questions per subject
    const availableBySubject = {};
    questions?.forEach(q => {
      if (!availableBySubject[q.subjectId]) availableBySubject[q.subjectId] = 0;
      availableBySubject[q.subjectId]++;
    });

    // Fallback: calculate others' average correct % per subject from questions
    const othersStatsBySubject = {};
    questions?.forEach(q => {
      const sId = q.subjectId;
      if (!sId) return;
      if (!othersStatsBySubject[sId]) othersStatsBySubject[sId] = { sum: 0, count: 0 };
      const timesAnswered = q.timesAnswered || 0;
      const timesCorrect = q.timesCorrect || 0;
      if (timesAnswered > 0) {
        othersStatsBySubject[sId].sum += (timesCorrect / timesAnswered) * 100;
        othersStatsBySubject[sId].count++;
      }
    });

    // Build subject list: start with all subjects, merge cached perf data
    const subjectResults = [];
    const processedIds = new Set();

    subjects?.forEach(subject => {
      const displayName = getDisplayName(subject, activeSpecialty);
      const cached = cachedSubjectPerf[subject.subjectId] || {};
      const totalQ = cached.totalQuestions || 0;
      const correct = cached.correct || 0;
      const incorrect = cached.incorrect || 0;
      const omitted = cached.omitted || 0;
      const available = availableBySubject[subject.subjectId] || 0;

      // P-Rank calculation — prefer real distribution
      const userCorrectPercent = totalQ > 0 ? (correct / totalQ) * 100 : 0;
      let pRank = null;

      const subjectDist = peerSubjectDists[subject.subjectId];
      if (totalQ > 0 && subjectDist?.totalUsers > 0 && subjectDist?.buckets) {
        // Real percentile from distribution
        const percentile = computePercentile(userCorrectPercent, subjectDist.buckets, subjectDist.totalUsers);
        if (percentile !== null) {
          pRank = Math.min(100, Math.max(1, 101 - percentile));
        }
      } else if (totalQ > 0) {
        // Fallback: old heuristic from question analytics
        const othersData = othersStatsBySubject[subject.subjectId];
        if (othersData?.count > 0) {
          const othersAvgCorrectPercent = othersData.sum / othersData.count;
          const difference = userCorrectPercent - othersAvgCorrectPercent;
          const percentile = Math.min(100, Math.max(0, Math.round(50 + difference)));
          pRank = Math.min(100, Math.max(1, 101 - percentile));
        }
      }

      processedIds.add(subject.subjectId);
      subjectResults.push({
        subjectId: subject.subjectId,
        name: displayName,
        totalQuestions: totalQ,
        correct, incorrect, omitted,
        usage: `${totalQ}/${available}`,
        usagePercent: available > 0 ? Math.round((totalQ / available) * 100) : 0,
        correctPercent: totalQ > 0 ? Math.round((correct / totalQ) * 100) : 0,
        incorrectPercent: totalQ > 0 ? Math.round((incorrect / totalQ) * 100) : 0,
        omittedPercent: totalQ > 0 ? Math.round((omitted / totalQ) * 100) : 0,
        pRank
      });
    });

    // Add any subjects from cache that weren't in subjects list
    Object.entries(cachedSubjectPerf).forEach(([sId, cached]) => {
      if (processedIds.has(sId)) return;
      const totalQ = cached.totalQuestions || 0;
      subjectResults.push({
        subjectId: sId, name: sId,
        totalQuestions: totalQ, correct: cached.correct || 0,
        incorrect: cached.incorrect || 0, omitted: cached.omitted || 0,
        usage: `${totalQ}/0`, usagePercent: 0,
        correctPercent: totalQ > 0 ? Math.round(((cached.correct || 0) / totalQ) * 100) : 0,
        incorrectPercent: totalQ > 0 ? Math.round(((cached.incorrect || 0) / totalQ) * 100) : 0,
        omittedPercent: totalQ > 0 ? Math.round(((cached.omitted || 0) / totalQ) * 100) : 0,
        pRank: null
      });
    });

    return subjectResults.sort((a, b) => b.totalQuestions - a.totalQuestions);
  }, [userPerformance, subjects, questions, peerDistribution]);

  // Calculate unit performance from cached data + P-Rank from questions
  const unitPerformance = useMemo(() => {
    const cachedUnitPerf = userPerformance?.unitPerformance || {};

    // Calculate available questions per unit + others' stats
    const availableByUnit = {};
    const othersStatsByUnit = {};
    questions?.forEach(q => {
      if (q.unitId) {
        if (!availableByUnit[q.unitId]) availableByUnit[q.unitId] = 0;
        availableByUnit[q.unitId]++;
        if (!othersStatsByUnit[q.unitId]) othersStatsByUnit[q.unitId] = { sum: 0, count: 0 };
        const timesAnswered = q.timesAnswered || 0;
        const timesCorrect = q.timesCorrect || 0;
        if (timesAnswered > 0) {
          othersStatsByUnit[q.unitId].sum += (timesCorrect / timesAnswered) * 100;
          othersStatsByUnit[q.unitId].count++;
        }
      }
    });

    // Build unit map grouped by subject
    const result = {};
    units?.forEach(unit => {
      const subjectId = unit.subjectId;
      const displayName = getDisplayName(unit, activeSpecialty);
      const cached = cachedUnitPerf[unit.unitId] || {};
      const totalQ = cached.totalQuestions || 0;
      const correct = cached.correct || 0;
      const incorrect = cached.incorrect || 0;
      const omitted = cached.omitted || 0;
      const available = availableByUnit[unit.unitId] || 0;

      const userCorrectPercent = totalQ > 0 ? (correct / totalQ) * 100 : 0;
      const othersData = othersStatsByUnit[unit.unitId];
      const othersAvgCorrectPercent = othersData?.count > 0 ? othersData.sum / othersData.count : 0;
      let pRank = null;
      if (totalQ > 0 && othersData?.count > 0) {
        const difference = userCorrectPercent - othersAvgCorrectPercent;
        const percentile = Math.min(100, Math.max(0, Math.round(50 + difference)));
        pRank = Math.min(100, Math.max(1, 101 - percentile));
      }

      if (!result[subjectId]) result[subjectId] = [];
      result[subjectId].push({
        unitId: unit.unitId, subjectId, name: displayName,
        totalQuestions: totalQ, correct, incorrect, omitted,
        usage: `${totalQ}/${available}`, usagePercent: available > 0 ? Math.round((totalQ / available) * 100) : 0,
        correctPercent: totalQ > 0 ? Math.round((correct / totalQ) * 100) : 0,
        incorrectPercent: totalQ > 0 ? Math.round((incorrect / totalQ) * 100) : 0,
        omittedPercent: totalQ > 0 ? Math.round((omitted / totalQ) * 100) : 0,
        pRank
      });
    });

    // Sort each subject's units
    Object.keys(result).forEach(sId => {
      result[sId].sort((a, b) => b.totalQuestions - a.totalQuestions);
    });

    return result;
  }, [userPerformance, units, questions]);

  // Calculate topic performance from cached data + P-Rank from questions
  const topicPerformance = useMemo(() => {
    const cachedTopicPerf = userPerformance?.topicPerformance || {};

    // Calculate available questions per topic + others' stats
    const availableByTopic = {};
    const othersStatsByTopic = {};
    questions?.forEach(q => {
      if (q.topicId) {
        if (!availableByTopic[q.topicId]) availableByTopic[q.topicId] = 0;
        availableByTopic[q.topicId]++;
        if (!othersStatsByTopic[q.topicId]) othersStatsByTopic[q.topicId] = { sum: 0, count: 0 };
        const timesAnswered = q.timesAnswered || 0;
        const timesCorrect = q.timesCorrect || 0;
        if (timesAnswered > 0) {
          othersStatsByTopic[q.topicId].sum += (timesCorrect / timesAnswered) * 100;
          othersStatsByTopic[q.topicId].count++;
        }
      }
    });

    // Build topic map grouped by unit
    const result = {};
    topics?.forEach(topic => {
      const unitId = topic.unitId;
      const displayName = getDisplayName(topic, activeSpecialty);
      const cached = cachedTopicPerf[topic.topicId] || {};
      const totalQ = cached.totalQuestions || 0;
      const correct = cached.correct || 0;
      const incorrect = cached.incorrect || 0;
      const omitted = cached.omitted || 0;
      const available = availableByTopic[topic.topicId] || 0;

      const userCorrectPercent = totalQ > 0 ? (correct / totalQ) * 100 : 0;
      const othersData = othersStatsByTopic[topic.topicId];
      const othersAvgCorrectPercent = othersData?.count > 0 ? othersData.sum / othersData.count : 0;
      let pRank = null;
      if (totalQ > 0 && othersData?.count > 0) {
        const difference = userCorrectPercent - othersAvgCorrectPercent;
        const percentile = Math.min(100, Math.max(0, Math.round(50 + difference)));
        pRank = Math.min(100, Math.max(1, 101 - percentile));
      }

      if (!result[unitId]) result[unitId] = [];
      result[unitId].push({
        topicId: topic.topicId, unitId, subjectId: topic.subjectId, name: displayName,
        totalQuestions: totalQ, correct, incorrect, omitted,
        usage: `${totalQ}/${available}`, usagePercent: available > 0 ? Math.round((totalQ / available) * 100) : 0,
        correctPercent: totalQ > 0 ? Math.round((correct / totalQ) * 100) : 0,
        incorrectPercent: totalQ > 0 ? Math.round((incorrect / totalQ) * 100) : 0,
        omittedPercent: totalQ > 0 ? Math.round((omitted / totalQ) * 100) : 0,
        pRank
      });
    });

    // Sort each unit's topics
    Object.keys(result).forEach(uId => {
      result[uId].sort((a, b) => b.totalQuestions - a.totalQuestions);
    });

    return result;
  }, [userPerformance, topics, questions]);

  // Safe date parser: handles millis (number), ISO strings, and Firestore Timestamp objects
  const safeParseDate = (value) => {
    if (!value) return null;
    if (typeof value === 'number') return new Date(value);
    if (typeof value === 'string') return new Date(value);
    if (value?.toMillis) return new Date(value.toMillis());
    if (value?.seconds) return new Date(value.seconds * 1000);
    return null;
  };

  // Performance by Date data for charts (using cached test summaries)
  const performanceByDate = useMemo(() => {
    if (!cachedTestSummaries.length) return { labels: [], data: [], cumulativeData: [] };

    // Filter tests by date range if set
    let filteredTests = cachedTestSummaries;
    if (dateRange.start || dateRange.end) {
      filteredTests = cachedTestSummaries.filter(test => {
        const testDate = safeParseDate(test.completedAt);
        if (!testDate || isNaN(testDate.getTime())) return false;
        if (dateRange.start && testDate < new Date(dateRange.start)) return false;
        if (dateRange.end && testDate > new Date(dateRange.end + 'T23:59:59')) return false;
        return true;
      });
    }

    if (!filteredTests.length) return { labels: [], data: [], cumulativeData: [] };

    // Group tests by date
    const dateGroups = {};
    filteredTests.forEach(test => {
      if (!test.completedAt) return;
      const date = safeParseDate(test.completedAt);
      if (!date || isNaN(date.getTime())) return;
      const dateKey = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

      if (!dateGroups[dateKey]) {
        dateGroups[dateKey] = { scores: [], date };
      }
      dateGroups[dateKey].scores.push(test.score || 0);
    });

    // Sort by date and calculate average per date
    const sortedDates = Object.entries(dateGroups)
      .sort((a, b) => a[1].date - b[1].date)
      .slice(-15);

    // Calculate cumulative score
    let cumulativeTotal = 0;
    let cumulativeCount = 0;
    const cumulativeData = sortedDates.map(([, group]) => {
      group.scores.forEach(score => {
        cumulativeTotal += score;
        cumulativeCount++;
      });
      return Math.round(cumulativeTotal / cumulativeCount);
    });

    return {
      labels: sortedDates.map(([dateKey]) => dateKey),
      data: sortedDates.map(([, group]) =>
        Math.round(group.scores.reduce((a, b) => a + b, 0) / group.scores.length)
      ),
      cumulativeData
    };
  }, [cachedTestSummaries, dateRange]);

  // Daily activity data for heat map and streak tracking
  const dailyActivity = useMemo(() => {
    // Build day map: { 'YYYY-MM-DD': totalQuestionsCount }
    const dayMap = {};
    cachedTestSummaries.forEach(test => {
      const date = safeParseDate(test.completedAt);
      if (!date || isNaN(date.getTime())) return;
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      dayMap[key] = (dayMap[key] || 0) + (test.totalQuestions || 0);
    });

    // Calculate streaks
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayKey = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;

    // Current streak: consecutive days ending today or yesterday
    let streak = 0;
    let checkDate = new Date(today);
    // Start from today; if no activity today, try starting from yesterday
    if (!dayMap[todayKey] && dayMap[yesterdayKey]) {
      checkDate = new Date(yesterday);
    }
    while (true) {
      const key = `${checkDate.getFullYear()}-${String(checkDate.getMonth() + 1).padStart(2, '0')}-${String(checkDate.getDate()).padStart(2, '0')}`;
      if (dayMap[key]) {
        streak++;
        checkDate.setDate(checkDate.getDate() - 1);
      } else {
        break;
      }
    }

    // Longest streak: sort all active dates and find longest consecutive run
    const activeDates = Object.keys(dayMap).sort();
    let longestStreak = 0;
    let currentRun = 0;
    for (let i = 0; i < activeDates.length; i++) {
      if (i === 0) {
        currentRun = 1;
      } else {
        const prev = new Date(activeDates[i - 1] + 'T00:00:00');
        const curr = new Date(activeDates[i] + 'T00:00:00');
        const diffDays = Math.round((curr - prev) / (1000 * 60 * 60 * 24));
        currentRun = diffDays === 1 ? currentRun + 1 : 1;
      }
      longestStreak = Math.max(longestStreak, currentRun);
    }

    // Heat map: last 90 days
    const heatMapDays = [];
    for (let i = 89; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      heatMapDays.push({
        date: new Date(d),
        dateKey: key,
        count: dayMap[key] || 0,
        dayOfWeek: d.getDay() // 0=Sun, 6=Sat
      });
    }

    const maxCount = Math.max(...heatMapDays.map(d => d.count), 1);

    // This week total (Sun–Sat)
    const startOfWeek = new Date(today);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
    let weekTotal = 0;
    for (let i = 0; i < 7; i++) {
      const d = new Date(startOfWeek);
      d.setDate(d.getDate() + i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      weekTotal += dayMap[key] || 0;
    }

    const todayTotal = dayMap[todayKey] || 0;

    return { dayMap, streak, longestStreak, heatMapDays, maxCount, weekTotal, todayTotal };
  }, [cachedTestSummaries]);

  // Questions Answered bar chart data (week/month/year with navigation)
  const questionsAnsweredData = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let labels = [];
    let dateBuckets = []; // array of { key, label, startDate, endDate }

    if (activityView === 'week') {
      // Find start of current week (Sunday), then apply offset
      const startOfWeek = new Date(today);
      startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay() + (periodOffset * 7));
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6);

      const dayAbbrs = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
      for (let i = 0; i < 7; i++) {
        const d = new Date(startOfWeek);
        d.setDate(d.getDate() + i);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        dateBuckets.push({ key, date: new Date(d), label: dayAbbrs[i] });
        labels.push(dayAbbrs[i]);
      }

      const rangeLabel = `${startOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      const counts = dateBuckets.map(b => dailyActivity.dayMap[b.key] || 0);
      const total = counts.reduce((a, b) => a + b, 0);

      // Most answered in one day within this week
      let maxDay = { count: 0, date: null };
      dateBuckets.forEach((b, i) => {
        if (counts[i] > maxDay.count) {
          maxDay = { count: counts[i], date: b.date };
        }
      });

      // Days active this week
      const daysActive = counts.filter(c => c > 0).length;

      return { labels, counts, rangeLabel, total, maxDay, daysActive };
    } else if (activityView === 'month') {
      // Current month + offset
      const targetMonth = new Date(today.getFullYear(), today.getMonth() + periodOffset, 1);
      const year = targetMonth.getFullYear();
      const month = targetMonth.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();

      for (let i = 1; i <= daysInMonth; i++) {
        const d = new Date(year, month, i);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        dateBuckets.push({ key, date: new Date(d), label: String(i) });
        labels.push(i % 5 === 1 || i === daysInMonth ? String(i) : '');
      }

      const rangeLabel = targetMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      const counts = dateBuckets.map(b => dailyActivity.dayMap[b.key] || 0);
      const total = counts.reduce((a, b) => a + b, 0);

      let maxDay = { count: 0, date: null };
      dateBuckets.forEach((b, i) => {
        if (counts[i] > maxDay.count) {
          maxDay = { count: counts[i], date: b.date };
        }
      });

      const daysActive = counts.filter(c => c > 0).length;

      return { labels, counts, rangeLabel, total, maxDay, daysActive };
    } else {
      // Year view — group by month
      const targetYear = today.getFullYear() + periodOffset;
      const monthAbbrs = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

      const counts = new Array(12).fill(0);
      let maxDay = { count: 0, date: null };
      let daysActive = 0;

      for (let m = 0; m < 12; m++) {
        const daysInMonth = new Date(targetYear, m + 1, 0).getDate();
        for (let d = 1; d <= daysInMonth; d++) {
          const date = new Date(targetYear, m, d);
          const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
          const dayCount = dailyActivity.dayMap[key] || 0;
          counts[m] += dayCount;
          if (dayCount > 0) daysActive++;
          if (dayCount > maxDay.count) {
            maxDay = { count: dayCount, date };
          }
        }
        labels.push(monthAbbrs[m]);
      }

      const rangeLabel = String(targetYear);
      const total = counts.reduce((a, b) => a + b, 0);

      return { labels, counts, rangeLabel, total, maxDay, daysActive };
    }
  }, [dailyActivity.dayMap, activityView, periodOffset]);

  // Performance by Test data for charts (using cached test summaries)
  const performanceByTest = useMemo(() => {
    if (!cachedTestSummaries.length) return { labels: [], data: [], othersAvgData: [], cumulativeData: [] };

    // Filter tests by date range if set
    let filteredTests = cachedTestSummaries;
    if (testDateRange.start || testDateRange.end) {
      filteredTests = cachedTestSummaries.filter(test => {
        const testDate = safeParseDate(test.completedAt);
        if (!testDate || isNaN(testDate.getTime())) return false;
        if (testDateRange.start && testDate < new Date(testDateRange.start)) return false;
        if (testDateRange.end && testDate > new Date(testDateRange.end + 'T23:59:59')) return false;
        return true;
      });
    }

    if (!filteredTests.length) return { labels: [], data: [], othersAvgData: [], cumulativeData: [] };

    // Sort tests by test number and take last 20
    const sortedTests = [...filteredTests]
      .sort((a, b) => (a.testNumber || 0) - (b.testNumber || 0))
      .slice(-20);

    // Others' average — not available per-test from cache, use overall avg
    const othersAvgData = sortedTests.map(() => overallStats.othersAvgScore || 0);

    // Calculate cumulative score
    let cumulativeTotal = 0;
    let cumulativeCount = 0;
    const cumulativeData = sortedTests.map(test => {
      cumulativeTotal += test.score || 0;
      cumulativeCount++;
      return Math.round(cumulativeTotal / cumulativeCount);
    });

    return {
      labels: sortedTests.map(t => `Test ${t.testNumber || 'N/A'}`),
      data: sortedTests.map(t => t.score || 0),
      othersAvgData,
      cumulativeData
    };
  }, [cachedTestSummaries, overallStats.othersAvgScore, testDateRange]);

  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  // Bell curve data — use real histogram when available, otherwise synthetic normal curve
  const bellCurveData = useMemo(() => {
    const labels = [];
    const points = [];

    // Generate 21 labels from 0% to 100%
    for (let x = 0; x <= 100; x += 5) {
      labels.push(`${x}%`);
    }

    if (peerDistribution?.available && peerDistribution.overallBuckets) {
      // Real histogram data from peer distribution
      const buckets = peerDistribution.overallBuckets;
      const maxCount = Math.max(...buckets, 1);
      for (let i = 0; i < 21; i++) {
        // Map 21 labels to 20 buckets: last label (100%) uses bucket 19
        const bucketIdx = Math.min(i, 19);
        points.push((buckets[bucketIdx] / maxCount) * 100); // Scale to percentage of max for visibility
      }
    } else {
      // Fallback: synthetic normal distribution
      const mean = overallStats.othersAvgScore !== null ? overallStats.othersAvgScore : 50;
      const stdDev = 15;
      for (let x = 0; x <= 100; x += 5) {
        const exponent = -Math.pow(x - mean, 2) / (2 * Math.pow(stdDev, 2));
        const y = Math.exp(exponent);
        points.push(y * 100);
      }
    }

    // Find the index closest to user's score for highlighting
    const userScoreIndex = Math.round(overallStats.yourScore / 5);

    // Find the index closest to peers' average
    const mean = overallStats.othersAvgScore !== null ? overallStats.othersAvgScore : 50;
    const peersAvgIndex = Math.round(mean / 5);

    return { labels, points, userScoreIndex, peersAvgIndex };
  }, [overallStats.yourScore, overallStats.othersAvgScore, peerDistribution]);

  // Index for peers' average line on the bell curve
  const peersAvgIndex = bellCurveData.peersAvgIndex;

  // Bell curve chart data
  const percentileChartData = {
    labels: bellCurveData.labels,
    datasets: [
      // Bell curve fill
      {
        label: 'Distribution',
        data: bellCurveData.points,
        borderColor: 'rgba(94, 114, 228, 1)',
        backgroundColor: 'rgba(94, 114, 228, 0.2)',
        fill: true,
        lineTension: 0.4,
        pointRadius: 0,
        borderWidth: 2
      },
      // Peers' average vertical line (dashed)
      {
        label: `Peers' Avg (${overallStats.othersAvgScore !== null ? overallStats.othersAvgScore : 50}%)`,
        data: bellCurveData.points.map((point, index) => {
          if (index === peersAvgIndex) return point;
          if (index === peersAvgIndex - 1 || index === peersAvgIndex + 1) return null;
          return null;
        }),
        borderColor: 'rgba(136, 152, 170, 0.9)',
        backgroundColor: 'transparent',
        pointRadius: bellCurveData.points.map((_, index) =>
          index === peersAvgIndex ? 6 : 0
        ),
        pointBackgroundColor: 'rgba(136, 152, 170, 0.9)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        borderWidth: 0,
        fill: false
      },
      // User's score point
      {
        label: 'Your Score',
        data: bellCurveData.points.map((point, index) =>
          index === bellCurveData.userScoreIndex ? point : null
        ),
        borderColor: '#f5365c',
        backgroundColor: 'transparent',
        pointRadius: bellCurveData.points.map((_, index) =>
          index === bellCurveData.userScoreIndex ? 10 : 0
        ),
        pointBackgroundColor: '#f5365c',
        pointBorderColor: '#fff',
        pointBorderWidth: 3,
        borderWidth: 0,
        fill: false
      }
    ]
  };

  // Custom plugin to draw vertical lines
  const verticalLinePlugin = {
    afterDraw: (chart) => {
      const ctx = chart.ctx;
      const xAxis = chart.scales['x-axis-0'];
      const yAxis = chart.scales['y-axis-0'];

      if (!xAxis || !yAxis) return;

      // Draw peers' average line (dashed gray)
      const medianX = xAxis.getPixelForValue(bellCurveData.labels[peersAvgIndex]);
      ctx.save();
      ctx.beginPath();
      ctx.setLineDash([5, 5]);
      ctx.strokeStyle = 'rgba(136, 152, 170, 0.9)';
      ctx.lineWidth = 2;
      ctx.moveTo(medianX, yAxis.top);
      ctx.lineTo(medianX, yAxis.bottom);
      ctx.stroke();
      ctx.restore();

      // Draw user score line (solid red)
      const userX = xAxis.getPixelForValue(bellCurveData.labels[bellCurveData.userScoreIndex]);
      ctx.save();
      ctx.beginPath();
      ctx.setLineDash([]);
      ctx.strokeStyle = '#f5365c';
      ctx.lineWidth = 2;
      ctx.moveTo(userX, yAxis.top);
      ctx.lineTo(userX, yAxis.bottom);
      ctx.stroke();
      ctx.restore();
    }
  };

  // Bell curve chart options
  const percentileChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: true,
      position: 'top',
      labels: {
        filter: (legendItem) => legendItem.datasetIndex !== 0,
        usePointStyle: true,
        boxWidth: 8
      }
    },
    tooltips: {
      filter: (tooltipItem) => tooltipItem.datasetIndex === 0,
      callbacks: {
        title: (tooltipItems) => tooltipItems && tooltipItems[0] ? `Score: ${tooltipItems[0].xLabel || tooltipItems[0].label}` : '',
        label: () => ''
      }
    },
    scales: {
      xAxes: [{
        gridLines: {
          display: true,
          color: 'rgba(0,0,0,0.05)'
        },
        scaleLabel: {
          display: true,
          labelString: 'Score %'
        }
      }],
      yAxes: [{
        display: false,
        gridLines: {
          display: false
        }
      }]
    },
  };

  // Chart.js v2 options for line chart
  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: true,
      position: 'top',
      labels: {
        usePointStyle: true,
        boxWidth: 8
      }
    },
    tooltips: {
      callbacks: {
        label: (tooltipItem, data) => `${data.datasets[tooltipItem.datasetIndex].label}: ${tooltipItem.yLabel}%`
      }
    },
    scales: {
      yAxes: [{
        ticks: {
          beginAtZero: true,
          max: 100,
          stepSize: 20,
          callback: (value) => `${value}%`
        }
      }]
    }
  };

  const lineChartData = {
    labels: performanceByDate.labels,
    datasets: [
      {
        label: 'Your Score',
        data: performanceByDate.data,
        borderColor: '#5e72e4',
        backgroundColor: 'rgba(94, 114, 228, 0.1)',
        fill: false,
        lineTension: 0.4,
        pointBackgroundColor: '#5e72e4',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4
      },
      {
        label: 'Cumulative Score',
        data: performanceByDate.cumulativeData,
        borderColor: '#2dce89',
        backgroundColor: 'rgba(45, 206, 137, 0.1)',
        fill: false,
        lineTension: 0.4,
        pointBackgroundColor: '#2dce89',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        borderDash: [5, 5]
      }
    ]
  };

  const testChartData = {
    labels: performanceByTest.labels,
    datasets: [
      {
        label: 'Your Score',
        data: performanceByTest.data,
        borderColor: '#5e72e4',
        backgroundColor: 'rgba(94, 114, 228, 0.1)',
        fill: false,
        lineTension: 0.4,
        pointBackgroundColor: '#5e72e4',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4
      },
      {
        label: "Other's Average Score",
        data: performanceByTest.othersAvgData,
        borderColor: '#fb6340',
        backgroundColor: 'rgba(251, 99, 64, 0.1)',
        fill: false,
        lineTension: 0.4,
        pointBackgroundColor: '#fb6340',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4
      },
      {
        label: 'Your Cumulative Score',
        data: performanceByTest.cumulativeData,
        borderColor: '#2dce89',
        backgroundColor: 'rgba(45, 206, 137, 0.1)',
        fill: false,
        lineTension: 0.4,
        pointBackgroundColor: '#2dce89',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        borderDash: [5, 5]
      }
    ]
  };

  const testChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: true,
      position: 'top',
      labels: {
        usePointStyle: true,
        boxWidth: 8
      }
    },
    tooltips: {
      callbacks: {
        label: (tooltipItem, data) => `${data.datasets[tooltipItem.datasetIndex].label}: ${tooltipItem.yLabel}%`
      }
    },
    scales: {
      yAxes: [{
        ticks: {
          beginAtZero: true,
          max: 100,
          stepSize: 20,
          callback: (value) => `${value}%`
        }
      }]
    }
  };

  return (
    <>
      <Header />
      <AccessRestriction>
      <div className="container-fluid mt--7">
        <TrialBanner />
        <BoardsCountdownBanner />
        <AddonCrossSellBanner />
        <Row>
          <Col lg="12">
            <Card className="shadow">
              <CardHeader className="bg-transparent">
                <Row className="align-items-center">
                  <Col>
                    <h2 className="mb-0">
                      <i className="fas fa-chart-line mr-2"></i>
                      Performance Analytics
                    </h2>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center py-5">
                    <i className="fas fa-spinner fa-spin fa-3x text-muted mb-3"></i>
                    <p className="text-muted">Loading performance data...</p>
                  </div>
                ) : !overallStats.testsCompleted ? (
                  <div className="text-center py-5">
                    <i className="fas fa-chart-bar fa-4x text-muted mb-3"></i>
                    <h3 className="text-muted">No Completed Tests Yet</h3>
                    <p className="text-muted">
                      Complete some tests to see your performance analytics.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Tabs Navigation */}
                    <Nav tabs className="mb-4">
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === 'overall' })}
                          onClick={() => toggle('overall')}
                          style={{ cursor: 'pointer' }}
                        >
                          <i className="fas fa-tachometer-alt mr-2"></i>
                          Overall
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === 'reports' })}
                          onClick={() => toggle('reports')}
                          style={{ cursor: 'pointer' }}
                        >
                          <i className="fas fa-table mr-2"></i>
                          Reports
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === 'graphs' })}
                          onClick={() => toggle('graphs')}
                          style={{ cursor: 'pointer' }}
                        >
                          <i className="fas fa-chart-area mr-2"></i>
                          Graphs
                        </NavLink>
                      </NavItem>
                    </Nav>

                    <TabContent activeTab={activeTab}>
                      {/* Overall Tab */}
                      <TabPane tabId="overall">
                        {/* Questions Answered Bar Chart */}
                        <Row className="mb-4">
                          <Col lg="12">
                            <Card className="shadow-sm">
                              <CardHeader className="border-0 pb-0">
                                <div className="d-flex justify-content-between align-items-center flex-wrap">
                                  <h4 className="mb-0">
                                    <i className="fas fa-chart-bar text-primary mr-2"></i>
                                    Questions Answered
                                  </h4>
                                  <div className="btn-group btn-group-sm" role="group">
                                    {['week', 'month', 'year'].map(view => (
                                      <button
                                        key={view}
                                        type="button"
                                        className={`btn btn-sm ${activityView === view ? 'btn-default' : 'btn-outline-default'}`}
                                        onClick={() => { setActivityView(view); setPeriodOffset(0); }}
                                        style={{ textTransform: 'capitalize', minWidth: '60px' }}
                                      >
                                        {view.charAt(0).toUpperCase() + view.slice(1)}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              </CardHeader>
                              <CardBody>
                                <div style={{ height: '220px' }}>
                                  <Bar
                                    data={{
                                      labels: questionsAnsweredData.labels,
                                      datasets: [{
                                        label: 'Questions',
                                        data: questionsAnsweredData.counts,
                                        backgroundColor: questionsAnsweredData.counts.map((c) => {
                                          const max = Math.max(...questionsAnsweredData.counts, 1);
                                          if (c === 0) return '#e9ecef';
                                          const ratio = c / max;
                                          if (ratio <= 0.33) return '#9be9a8';
                                          if (ratio <= 0.66) return '#40c463';
                                          return '#30a14e';
                                        }),
                                        borderRadius: 3,
                                        barPercentage: activityView === 'month' ? 0.9 : 0.6,
                                        categoryPercentage: activityView === 'month' ? 0.95 : 0.7
                                      }]
                                    }}
                                    options={{
                                      responsive: true,
                                      maintainAspectRatio: false,
                                      legend: { display: false },
                                      tooltips: {
                                        callbacks: {
                                          title: (items) => {
                                            if (!items.length) return '';
                                            const idx = items[0].index;
                                            if (activityView === 'week') {
                                              const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                                              return dayNames[idx];
                                            }
                                            return items[0].label;
                                          },
                                          label: (item) => `${item.yLabel} questions`
                                        }
                                      },
                                      scales: {
                                        yAxes: [{
                                          ticks: {
                                            beginAtZero: true,
                                            precision: 0,
                                            maxTicksLimit: 5
                                          },
                                          gridLines: {
                                            color: 'rgba(0,0,0,0.05)'
                                          }
                                        }],
                                        xAxes: [{
                                          gridLines: { display: false },
                                          ticks: {
                                            maxRotation: 0,
                                            autoSkip: activityView === 'month',
                                            maxTicksLimit: activityView === 'month' ? 10 : undefined
                                          }
                                        }]
                                      }
                                    }}
                                  />
                                </div>
                                {/* Navigation + Date Range */}
                                <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap">
                                  <span className="text-muted" style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                                    {questionsAnsweredData.rangeLabel}
                                  </span>
                                  <div className="d-flex align-items-center">
                                    <button
                                      className="btn btn-sm btn-outline-default rounded-circle p-0 d-flex align-items-center justify-content-center"
                                      style={{ width: '32px', height: '32px' }}
                                      onClick={() => setPeriodOffset(prev => prev - 1)}
                                    >
                                      <i className="fas fa-chevron-left" style={{ fontSize: '0.7rem' }}></i>
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-default mx-2"
                                      onClick={() => setPeriodOffset(0)}
                                      disabled={periodOffset === 0}
                                    >
                                      Today
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-default rounded-circle p-0 d-flex align-items-center justify-content-center"
                                      style={{ width: '32px', height: '32px' }}
                                      onClick={() => setPeriodOffset(prev => Math.min(prev + 1, 0))}
                                      disabled={periodOffset >= 0}
                                    >
                                      <i className="fas fa-chevron-right" style={{ fontSize: '0.7rem' }}></i>
                                    </button>
                                  </div>
                                </div>
                                {/* Summary Stats */}
                                <Row className="mt-3">
                                  <Col md="4" sm="4">
                                    <div className="text-center py-2" style={{ borderTop: '3px solid #2dce89' }}>
                                      <h2 className="mb-0 font-weight-bold">{questionsAnsweredData.total}</h2>
                                      <small className="text-muted">Questions Answered<br/>This {activityView.charAt(0).toUpperCase() + activityView.slice(1)}</small>
                                    </div>
                                  </Col>
                                  <Col md="4" sm="4">
                                    <div className="text-center py-2" style={{ borderTop: '3px solid #32325d' }}>
                                      <h2 className="mb-0 font-weight-bold">
                                        {questionsAnsweredData.maxDay.count}
                                        {questionsAnsweredData.maxDay.date && (
                                          <small className="text-muted ml-1" style={{ fontSize: '0.5em' }}>
                                            {questionsAnsweredData.maxDay.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                          </small>
                                        )}
                                      </h2>
                                      <small className="text-muted">Most Answered<br/>In One Day</small>
                                    </div>
                                  </Col>
                                  <Col md="4" sm="4">
                                    <div className="text-center py-2" style={{ borderTop: '3px solid #5e72e4' }}>
                                      <h2 className="mb-0 font-weight-bold">{questionsAnsweredData.daysActive}</h2>
                                      <small className="text-muted">Days<br/>Active</small>
                                    </div>
                                  </Col>
                                </Row>
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>

                        {/* Score Summary Row */}
                        <Row className="mb-4">
                          <Col lg="3" md="3" sm="6" className="mb-3">
                            <Card className="bg-gradient-success shadow-sm h-100">
                              <CardBody className="py-3 text-white">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <p className="text-white mb-1 small text-uppercase">Correct</p>
                                    <h2 className="mb-0 font-weight-bold text-white">{overallStats.totalCorrect}</h2>
                                  </div>
                                  <div className="icon icon-shape bg-white text-success rounded-circle shadow">
                                    <i className="fas fa-check"></i>
                                  </div>
                                </div>
                              </CardBody>
                            </Card>
                          </Col>
                          <Col lg="3" md="3" sm="6" className="mb-3">
                            <Card className="bg-gradient-danger shadow-sm h-100">
                              <CardBody className="py-3 text-white">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <p className="text-white mb-1 small text-uppercase">Incorrect</p>
                                    <h2 className="mb-0 font-weight-bold text-white">{overallStats.totalIncorrect}</h2>
                                  </div>
                                  <div className="icon icon-shape bg-white text-danger rounded-circle shadow">
                                    <i className="fas fa-times"></i>
                                  </div>
                                </div>
                              </CardBody>
                            </Card>
                          </Col>
                          <Col lg="3" md="3" sm="6" className="mb-3">
                            <Card className="bg-gradient-warning shadow-sm h-100">
                              <CardBody className="py-3 text-white">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <p className="text-white mb-1 small text-uppercase">Omitted</p>
                                    <h2 className="mb-0 font-weight-bold text-white">{overallStats.totalOmitted}</h2>
                                  </div>
                                  <div className="icon icon-shape bg-white text-warning rounded-circle shadow">
                                    <i className="fas fa-minus"></i>
                                  </div>
                                </div>
                              </CardBody>
                            </Card>
                          </Col>
                          <Col lg="3" md="3" sm="6" className="mb-3">
                            <Card className="bg-gradient-primary shadow-sm h-100">
                              <CardBody className="py-3 text-white">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <p className="text-white mb-1 small text-uppercase">Your Avg Score</p>
                                    <h2 className="mb-0 font-weight-bold text-white">{overallStats.yourScore}%</h2>
                                  </div>
                                  <div className="icon icon-shape bg-white text-primary rounded-circle shadow">
                                    <i className="fas fa-percentage"></i>
                                  </div>
                                </div>
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>

                        {/* Activity Heat Map + Streak Cards */}
                        <ActivityStreaksCard testSummaries={cachedTestSummaries} />

                        {/* Main Stats Row */}
                        <Row>
                          {/* Answer Breakdown Card */}
                          <Col lg="4" md="6" className="mb-4">
                            <Card className="shadow-sm h-100">
                              <CardHeader className="border-0">
                                <h4 className="mb-0">
                                  <i className="fas fa-chart-pie text-info mr-2"></i>
                                  Answer Breakdown
                                </h4>
                              </CardHeader>
                              <CardBody className="pt-0 d-flex align-items-center justify-content-center" style={{ minHeight: '280px' }}>
                                {(overallStats.totalCorrect + overallStats.totalIncorrect + overallStats.totalOmitted) > 0 ? (
                                  <div className="text-center w-100">
                                    <div style={{ width: '100%', maxWidth: '280px', margin: '0 auto' }}>
                                      <Doughnut
                                        data={{
                                          labels: ['Correct', 'Incorrect', 'Omitted'],
                                          datasets: [{
                                            data: [overallStats.totalCorrect, overallStats.totalIncorrect, overallStats.totalOmitted],
                                            backgroundColor: ['#2dce89', '#f5365c', '#fb6340'],
                                            borderWidth: 0
                                          }]
                                        }}
                                        options={{
                                          responsive: true,
                                          maintainAspectRatio: true,
                                          cutoutPercentage: 60,
                                          legend: { display: false },
                                          tooltips: {
                                            callbacks: {
                                              label: (tooltipItem, data) => {
                                                const dataset = data.datasets[tooltipItem.datasetIndex];
                                                const total = dataset.data.reduce((a, b) => a + b, 0);
                                                const value = dataset.data[tooltipItem.index];
                                                const percentage = Math.round((value / total) * 100);
                                                return `${data.labels[tooltipItem.index]}: ${value} (${percentage}%)`;
                                              }
                                            }
                                          }
                                        }}
                                      />
                                    </div>
                                    <div className="d-flex justify-content-center flex-wrap mt-3">
                                      <div className="d-flex align-items-center mx-2 text-nowrap">
                                        <span style={{ width: '10px', height: '10px', backgroundColor: '#2dce89', borderRadius: '2px', marginRight: '6px', flexShrink: 0 }}></span>
                                        <small className="text-muted">Correct:</small>
                                        <small className="ml-1 font-weight-bold">{overallStats.totalCorrect}</small>
                                      </div>
                                      <div className="d-flex align-items-center mx-2 text-nowrap">
                                        <span style={{ width: '10px', height: '10px', backgroundColor: '#f5365c', borderRadius: '2px', marginRight: '6px', flexShrink: 0 }}></span>
                                        <small className="text-muted">Incorrect:</small>
                                        <small className="ml-1 font-weight-bold">{overallStats.totalIncorrect}</small>
                                      </div>
                                      <div className="d-flex align-items-center mx-2 text-nowrap">
                                        <span style={{ width: '10px', height: '10px', backgroundColor: '#fb6340', borderRadius: '2px', marginRight: '6px', flexShrink: 0 }}></span>
                                        <small className="text-muted">Omitted:</small>
                                        <small className="ml-1 font-weight-bold">{overallStats.totalOmitted}</small>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-center text-muted py-3">
                                    <small>No answer data available</small>
                                  </div>
                                )}
                              </CardBody>
                            </Card>
                          </Col>

                          {/* QBank Usage Card */}
                          <Col lg="4" md="6" className="mb-4">
                            <Card className="shadow-sm h-100">
                              <CardHeader className="border-0">
                                <h4 className="mb-0">
                                  <i className="fas fa-database text-success mr-2"></i>
                                  QBank Usage
                                </h4>
                              </CardHeader>
                              <CardBody className="pt-0">
                                <Table borderless className="mb-0" style={{ fontSize: '1.05rem' }}>
                                  <tbody>
                                    <tr>
                                      <td>Used Questions</td>
                                      <td className="text-right">
                                        <Badge color="primary" pill style={{ fontSize: '0.7rem', padding: '0.35rem 0.7rem' }}>{overallStats.usedQuestions}</Badge>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>Unused Questions</td>
                                      <td className="text-right">
                                        <Badge color="primary" pill style={{ fontSize: '0.7rem', padding: '0.35rem 0.7rem' }}>{overallStats.unusedQuestions}</Badge>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>Total Questions</td>
                                      <td className="text-right">
                                        <Badge color="primary" pill style={{ fontSize: '0.7rem', padding: '0.35rem 0.7rem' }}>{overallStats.totalAvailableQuestions}</Badge>
                                      </td>
                                    </tr>
                                  </tbody>
                                </Table>
                                <Progress
                                  value={overallStats.totalAvailableQuestions > 0 ? (overallStats.usedQuestions / overallStats.totalAvailableQuestions) * 100 : 0}
                                  color="success"
                                  className="mt-3"
                                  style={{ height: '8px' }}
                                />
                                <small className="text-muted">{overallStats.totalAvailableQuestions > 0 ? Math.round((overallStats.usedQuestions / overallStats.totalAvailableQuestions) * 100) : 0}% Complete</small>
                              </CardBody>
                            </Card>
                          </Col>

                          {/* Test Count Card */}
                          <Col lg="4" md="6" className="mb-4">
                            <Card className="shadow-sm h-100">
                              <CardHeader className="border-0">
                                <h4 className="mb-0">
                                  <i className="fas fa-clipboard-list text-warning mr-2"></i>
                                  Test Count
                                </h4>
                              </CardHeader>
                              <CardBody className="pt-0">
                                <Table borderless className="mb-0" style={{ fontSize: '1.05rem' }}>
                                  <tbody>
                                    <tr>
                                      <td>Tests Created</td>
                                      <td className="text-right">
                                        <Badge color="primary" pill style={{ fontSize: '0.7rem', padding: '0.35rem 0.7rem' }}>{overallStats.testsCreated}</Badge>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>Tests Completed</td>
                                      <td className="text-right">
                                        <Badge color="primary" pill style={{ fontSize: '0.7rem', padding: '0.35rem 0.7rem' }}>{overallStats.testsCompleted}</Badge>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>Suspended Tests</td>
                                      <td className="text-right">
                                        <Badge color="primary" pill style={{ fontSize: '0.7rem', padding: '0.35rem 0.7rem' }}>{overallStats.suspendedTests}</Badge>
                                      </td>
                                    </tr>
                                  </tbody>
                                </Table>
                                <Progress
                                  value={overallStats.testsCreated > 0 ? (overallStats.testsCompleted / overallStats.testsCreated) * 100 : 0}
                                  color="success"
                                  className="mt-3"
                                  style={{ height: '8px' }}
                                />
                                <small className="text-muted">{overallStats.testsCreated > 0 ? Math.round((overallStats.testsCompleted / overallStats.testsCreated) * 100) : 0}% Completion Rate</small>
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>

                        {/* Percentile Rank Section */}
                        <Row>
                          <Col lg="12" className="mb-4">
                            <Card className="shadow-sm">
                              <CardHeader className="border-0">
                                <h4 className="mb-0">
                                  <i className="fas fa-trophy text-danger mr-2"></i>
                                  Performance Rank
                                </h4>
                              </CardHeader>
                              <CardBody>
                                <Row>
                                  <Col md="6" lg="3" className="mb-3 mb-lg-0">
                                    <div className="text-center p-3 border rounded">
                                      <small className="text-muted d-block mb-2">Your Average Score</small>
                                      <h2 className="mb-0 text-primary">{overallStats.yourScore}%</h2>
                                    </div>
                                  </Col>
                                  <Col md="6" lg="3" className="mb-3 mb-lg-0">
                                    <div className="text-center p-3 border rounded">
                                      <small className="text-muted d-block mb-2">Your Median Score</small>
                                      <h2 className="mb-0 text-warning">{overallStats.medianScore}%</h2>
                                    </div>
                                  </Col>
                                  <Col md="6" lg="3" className="mb-3 mb-md-0">
                                    <div className="text-center p-3 border rounded">
                                      <small className="text-muted d-block mb-2">Your Average Time Spent (sec)</small>
                                      <h2 className="mb-0 text-info">{overallStats.avgTimeSpent}</h2>
                                    </div>
                                  </Col>
                                  <Col md="6" lg="3">
                                    <div className="text-center p-3 border rounded">
                                      <small className="text-muted d-block mb-2">Peers' Avg Time Spent (sec)</small>
                                      <h2 className="mb-0 text-default">{overallStats.othersAvgTimeSpent > 0 ? overallStats.othersAvgTimeSpent : 'N/A'}</h2>
                                    </div>
                                  </Col>
                                </Row>
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>

                        {/* Peer Comparison Graph */}
                        <Row>
                          <Col lg="12" className="mb-4">
                            <Card className="shadow-sm">
                              <CardHeader className="bg-transparent border-0 pb-0">
                                <h4 className="mb-0">
                                  <i className="fas fa-chart-area text-info mr-2"></i>
                                  Peer Comparison
                                </h4>
                                {overallStats.peerPercentile !== null ? (
                                  <>
                                    <span className="text-muted" style={{ fontSize: '0.95rem' }}>
                                      <span className="text-danger">━</span> Your Avg Score ({overallStats.yourScore}%) &nbsp;•&nbsp;
                                      <span style={{ color: 'rgba(136, 152, 170, 0.9)' }}>┅</span> Peers' Avg ({overallStats.othersAvgScore}%)
                                    </span>
                                    <div className="mt-1">
                                      <Badge
                                        color={overallStats.peerPercentile >= 70 ? 'success' : overallStats.peerPercentile >= 40 ? 'warning' : 'danger'}
                                        style={{ fontSize: '0.8rem', padding: '0.35rem 0.7rem' }}
                                      >
                                        You are in the {getOrdinalSuffix(overallStats.peerPercentile)} percentile
                                      </Badge>
                                    </div>
                                  </>
                                ) : (
                                  <span className="text-muted" style={{ fontSize: '0.95rem' }}>
                                    Complete a test to see your peer comparison
                                  </span>
                                )}
                              </CardHeader>
                              <CardBody>
                                <div style={{ height: '280px' }}>
                                  <Line data={percentileChartData} options={percentileChartOptions} plugins={[verticalLinePlugin]} />
                                </div>
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>
                      </TabPane>

                      {/* Reports Tab */}
                      <TabPane tabId="reports">
                        <Card className="shadow-sm">
                          <CardHeader className="bg-transparent border-0">
                            <h4 className="mb-0">
                              <i className="fas fa-book text-primary mr-2"></i>
                              Performance by Subject
                            </h4>
                          </CardHeader>
                          <CardBody>
                            {subjectPerformance.length > 0 ? (
                              <div className="table-responsive">
                                <Table className="align-items-center table-flush">
                                  <thead className="thead-light">
                                    <tr>
                                      <th style={{ width: '40px' }}></th>
                                      <th>Name</th>
                                      <th>Usage</th>
                                      <th>Correct Q</th>
                                      <th>Incorrect Q</th>
                                      <th>Omitted Q</th>
                                      <th>P-Rank</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {subjectPerformance.map((subject, index) => (
                                      <React.Fragment key={index}>
                                        {/* Subject Row */}
                                        <tr
                                          onClick={() => toggleSubjectExpand(subject.subjectId)}
                                          style={{ cursor: 'pointer', backgroundColor: expandedSubjects[subject.subjectId] ? '#f6f9fc' : 'transparent' }}
                                          className="hover-bg-light"
                                        >
                                          <td className="text-center">
                                            <i
                                              className={`fas fa-chevron-${expandedSubjects[subject.subjectId] ? 'down' : 'right'} text-primary`}
                                              style={{ fontSize: '0.75rem', transition: 'transform 0.2s' }}
                                            ></i>
                                          </td>
                                          <td>
                                            <span className="font-weight-bold">{subject.name}</span>
                                            {unitPerformance[subject.subjectId]?.length > 0 && (
                                              <Badge color="light" className="ml-2" style={{ fontSize: '0.7rem' }}>
                                                {unitPerformance[subject.subjectId].length} units
                                              </Badge>
                                            )}
                                          </td>
                                          <td>
                                            <div className="d-flex align-items-center">
                                              <span className="mr-2">{subject.usage}</span>
                                              <Progress
                                                value={subject.usagePercent}
                                                style={{ width: '60px', height: '6px' }}
                                                color="primary"
                                              />
                                            </div>
                                          </td>
                                          <td>
                                            <span className="text-success font-weight-bold">
                                              {subject.correct} ({subject.correctPercent}%)
                                            </span>
                                          </td>
                                          <td>
                                            <span className="text-danger font-weight-bold">
                                              {subject.incorrect} ({subject.incorrectPercent}%)
                                            </span>
                                          </td>
                                          <td>
                                            <span className="text-warning font-weight-bold">
                                              {subject.omitted} ({subject.omittedPercent}%)
                                            </span>
                                          </td>
                                          <td>
                                            {subject.pRank !== null ? (
                                              <Badge
                                                color={subject.pRank <= 30 ? 'success' : subject.pRank <= 60 ? 'warning' : 'danger'}
                                                pill
                                              >
                                                {getOrdinalSuffix(subject.pRank)}
                                              </Badge>
                                            ) : (
                                              <Badge color="secondary" pill>-</Badge>
                                            )}
                                          </td>
                                        </tr>
                                        {/* Unit Rows (Expanded) */}
                                        {expandedSubjects[subject.subjectId] && unitPerformance[subject.subjectId]?.map((unit, unitIndex) => (
                                          <React.Fragment key={`${index}-${unitIndex}`}>
                                            <tr
                                              style={{ backgroundColor: '#f8f9fe', cursor: topicPerformance[unit.unitId]?.length > 0 ? 'pointer' : 'default' }}
                                              onClick={() => topicPerformance[unit.unitId]?.length > 0 && toggleUnitExpand(unit.unitId)}
                                            >
                                              <td className="text-center">
                                                {topicPerformance[unit.unitId]?.length > 0 && (
                                                  <i
                                                    className={`fas fa-chevron-${expandedUnits[unit.unitId] ? 'down' : 'right'} text-info ml-2`}
                                                    style={{ fontSize: '0.65rem', transition: 'transform 0.2s' }}
                                                  ></i>
                                                )}
                                              </td>
                                              <td>
                                                <span className="ml-3 text-muted">
                                                  <i className="fas fa-layer-group mr-2" style={{ fontSize: '0.75rem' }}></i>
                                                  {unit.name}
                                                  {topicPerformance[unit.unitId]?.length > 0 && (
                                                    <Badge color="light" className="ml-2" style={{ fontSize: '0.65rem' }}>
                                                      {topicPerformance[unit.unitId].length} topics
                                                    </Badge>
                                                  )}
                                                </span>
                                              </td>
                                              <td>
                                                <div className="d-flex align-items-center">
                                                  <span className="mr-2 text-muted">{unit.usage}</span>
                                                  <Progress
                                                    value={unit.usagePercent}
                                                    style={{ width: '60px', height: '6px' }}
                                                    color="info"
                                                  />
                                                </div>
                                              </td>
                                              <td>
                                                <span className="text-success">
                                                  {unit.correct} ({unit.correctPercent}%)
                                                </span>
                                              </td>
                                              <td>
                                                <span className="text-danger">
                                                  {unit.incorrect} ({unit.incorrectPercent}%)
                                                </span>
                                              </td>
                                              <td>
                                                <span className="text-warning">
                                                  {unit.omitted} ({unit.omittedPercent}%)
                                                </span>
                                              </td>
                                              <td>
                                                {unit.pRank !== null ? (
                                                  <Badge
                                                    color={unit.pRank <= 30 ? 'success' : unit.pRank <= 60 ? 'warning' : 'danger'}
                                                    pill
                                                  >
                                                    {getOrdinalSuffix(unit.pRank)}
                                                  </Badge>
                                                ) : (
                                                  <Badge color="secondary" pill>-</Badge>
                                                )}
                                              </td>
                                            </tr>
                                            {/* Topic Rows (Expanded under Unit) */}
                                            {expandedUnits[unit.unitId] && topicPerformance[unit.unitId]?.map((topic, topicIndex) => (
                                              <tr key={`${index}-${unitIndex}-${topicIndex}`} style={{ backgroundColor: '#f0f4f8' }}>
                                                <td></td>
                                                <td>
                                                  <span className="ml-5 text-muted" style={{ fontSize: '0.85rem' }}>
                                                    <i className="fas fa-tag mr-2" style={{ fontSize: '0.65rem' }}></i>
                                                    {topic.name}
                                                  </span>
                                                </td>
                                                <td>
                                                  <div className="d-flex align-items-center">
                                                    <span className="mr-2 text-muted" style={{ fontSize: '0.85rem' }}>{topic.usage}</span>
                                                    <Progress
                                                      value={topic.usagePercent}
                                                      style={{ width: '50px', height: '5px' }}
                                                      color="secondary"
                                                    />
                                                  </div>
                                                </td>
                                                <td>
                                                  <span className="text-success" style={{ fontSize: '0.85rem' }}>
                                                    {topic.correct} ({topic.correctPercent}%)
                                                  </span>
                                                </td>
                                                <td>
                                                  <span className="text-danger" style={{ fontSize: '0.85rem' }}>
                                                    {topic.incorrect} ({topic.incorrectPercent}%)
                                                  </span>
                                                </td>
                                                <td>
                                                  <span className="text-warning" style={{ fontSize: '0.85rem' }}>
                                                    {topic.omitted} ({topic.omittedPercent}%)
                                                  </span>
                                                </td>
                                                <td>
                                                  {topic.pRank !== null ? (
                                                    <Badge
                                                      color={topic.pRank <= 30 ? 'success' : topic.pRank <= 60 ? 'warning' : 'danger'}
                                                      pill
                                                      style={{ fontSize: '0.7rem' }}
                                                    >
                                                      {getOrdinalSuffix(topic.pRank)}
                                                    </Badge>
                                                  ) : (
                                                    <Badge color="secondary" pill style={{ fontSize: '0.7rem' }}>-</Badge>
                                                  )}
                                                </td>
                                              </tr>
                                            ))}
                                          </React.Fragment>
                                        ))}
                                        {expandedSubjects[subject.subjectId] && (!unitPerformance[subject.subjectId] || unitPerformance[subject.subjectId].length === 0) && (
                                          <tr style={{ backgroundColor: '#f8f9fe' }}>
                                            <td></td>
                                            <td colSpan="6" className="text-muted text-center py-2">
                                              <small>No unit data available for this subject</small>
                                            </td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            ) : (
                              <div className="text-center py-4">
                                <i className="fas fa-table fa-3x text-muted mb-3"></i>
                                <p className="text-muted">No subject data available yet.</p>
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      </TabPane>

                      {/* Graphs Tab */}
                      <TabPane tabId="graphs">
                        <Row>
                          {/* Performance by Date */}
                          <Col lg="12" className="mb-4">
                            <Card className="shadow-sm">
                              <CardHeader className="bg-transparent border-0">
                                <Row className="align-items-center">
                                  <Col md="6">
                                    <h4 className="mb-0">
                                      <i className="fas fa-calendar-alt text-primary mr-2"></i>
                                      Performance by Date
                                    </h4>
                                    <span className="text-muted" style={{ fontSize: '0.95rem' }}>
                                      {dateRange.start || dateRange.end
                                        ? `Filtered: ${dateRange.start || 'Start'} - ${dateRange.end || 'End'}`
                                        : 'Average score per day (last 15 days)'}
                                    </span>
                                  </Col>
                                  <Col md="6">
                                    <Row className="justify-content-end align-items-center">
                                      <Col md="5" sm="6" className="mb-2 mb-md-0">
                                        <FormGroup className="mb-0">
                                          <InputGroup className="input-group-alternative input-group-sm">
                                            <InputGroupAddon addonType="prepend">
                                              <InputGroupText>
                                                <i className="fas fa-calendar-alt text-primary" />
                                              </InputGroupText>
                                            </InputGroupAddon>
                                            <Input
                                              type="date"
                                              className="form-control-alternative"
                                              placeholder="Start Date"
                                              value={dateRange.start}
                                              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                                            />
                                          </InputGroup>
                                        </FormGroup>
                                      </Col>
                                      <Col md="5" sm="6" className="mb-2 mb-md-0">
                                        <FormGroup className="mb-0">
                                          <InputGroup className="input-group-alternative input-group-sm">
                                            <InputGroupAddon addonType="prepend">
                                              <InputGroupText>
                                                <i className="fas fa-calendar-alt text-primary" />
                                              </InputGroupText>
                                            </InputGroupAddon>
                                            <Input
                                              type="date"
                                              className="form-control-alternative"
                                              placeholder="End Date"
                                              value={dateRange.end}
                                              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                                            />
                                          </InputGroup>
                                        </FormGroup>
                                      </Col>
                                      {(dateRange.start || dateRange.end) && (
                                        <Col md="2" sm="12" className="d-flex align-items-center">
                                          <button
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => setDateRange({ start: '', end: '' })}
                                            title="Clear date filter"
                                          >
                                            <i className="fas fa-times"></i>
                                          </button>
                                        </Col>
                                      )}
                                    </Row>
                                  </Col>
                                </Row>
                              </CardHeader>
                              <CardBody>
                                {performanceByDate.labels.length > 0 ? (
                                  <div style={{ height: '300px' }}>
                                    <Line data={lineChartData} options={lineChartOptions} />
                                  </div>
                                ) : (
                                  <div className="text-center py-4">
                                    <i className="fas fa-chart-line fa-3x text-muted mb-3"></i>
                                    <p className="text-muted">Not enough data for date chart.</p>
                                  </div>
                                )}
                              </CardBody>
                            </Card>
                          </Col>

                          {/* Performance by Test */}
                          <Col lg="12">
                            <Card className="shadow-sm">
                              <CardHeader className="bg-transparent border-0">
                                <Row className="align-items-center">
                                  <Col md="6">
                                    <h4 className="mb-0">
                                      <i className="fas fa-chart-line text-success mr-2"></i>
                                      Performance by Test
                                    </h4>
                                    <span className="text-muted" style={{ fontSize: '0.95rem' }}>
                                      {testDateRange.start || testDateRange.end
                                        ? `Filtered: ${testDateRange.start || 'Start'} - ${testDateRange.end || 'End'}`
                                        : 'Score per test (last 20 tests)'}
                                    </span>
                                  </Col>
                                  <Col md="6">
                                    <Row className="justify-content-end align-items-center">
                                      <Col md="5" sm="6" className="mb-2 mb-md-0">
                                        <FormGroup className="mb-0">
                                          <InputGroup className="input-group-alternative input-group-sm">
                                            <InputGroupAddon addonType="prepend">
                                              <InputGroupText>
                                                <i className="fas fa-calendar-alt text-success" />
                                              </InputGroupText>
                                            </InputGroupAddon>
                                            <Input
                                              type="date"
                                              className="form-control-alternative"
                                              placeholder="Start Date"
                                              value={testDateRange.start}
                                              onChange={(e) => setTestDateRange(prev => ({ ...prev, start: e.target.value }))}
                                            />
                                          </InputGroup>
                                        </FormGroup>
                                      </Col>
                                      <Col md="5" sm="6" className="mb-2 mb-md-0">
                                        <FormGroup className="mb-0">
                                          <InputGroup className="input-group-alternative input-group-sm">
                                            <InputGroupAddon addonType="prepend">
                                              <InputGroupText>
                                                <i className="fas fa-calendar-alt text-success" />
                                              </InputGroupText>
                                            </InputGroupAddon>
                                            <Input
                                              type="date"
                                              className="form-control-alternative"
                                              placeholder="End Date"
                                              value={testDateRange.end}
                                              onChange={(e) => setTestDateRange(prev => ({ ...prev, end: e.target.value }))}
                                            />
                                          </InputGroup>
                                        </FormGroup>
                                      </Col>
                                      {(testDateRange.start || testDateRange.end) && (
                                        <Col md="2" sm="12" className="d-flex align-items-center">
                                          <button
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => setTestDateRange({ start: '', end: '' })}
                                            title="Clear date filter"
                                          >
                                            <i className="fas fa-times"></i>
                                          </button>
                                        </Col>
                                      )}
                                    </Row>
                                  </Col>
                                </Row>
                              </CardHeader>
                              <CardBody>
                                {performanceByTest.labels.length > 0 ? (
                                  <div style={{ height: '300px' }}>
                                    <Line data={testChartData} options={testChartOptions} />
                                  </div>
                                ) : (
                                  <div className="text-center py-4">
                                    <i className="fas fa-chart-line fa-3x text-muted mb-3"></i>
                                    <p className="text-muted">Not enough data for test chart.</p>
                                  </div>
                                )}
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>
                      </TabPane>
                    </TabContent>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
      </AccessRestriction>
    </>
  );
};

export default Performance;
